/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { OmcSettingsService } from '@src/app/pages/omc-settings/omc-settings.service';
import { maxChars, required } from '@src/app/shared/validators';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { AI_LEVEL_HINT, AI_LEVELS } from '@src/app/model/ai-level';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { RoleService } from '@src/app/pages/role-page/role.service';
import { Role } from '@src/app/model/role';
import { MatCardModule } from '@angular/material/card';
import { TextEditComponent } from '@src/app/shared/simpleFields/text-edit/text-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { EditFieldDialogComponent, EditFieldDialogOptions } from '@src/app/shared/dialogs/edit-field-dialog/edit-field-dialog.component';
import { Field } from '@src/app/shared/field/Field';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { CanComponentDeactivate } from '@src/app/shared/guards/can-deactivate.guard';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { OmcAddAgentComponent } from '@src/app/pages/omc-settings/omc-add-agent/omc-add-agent.component';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { AbstractObject } from '@src/app/model/abstract-object';
import { Agent } from '@src/app/model/Agent';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { Person } from '@src/app/model/person';
import { HttpParams } from '@angular/common/http';
import { uuid } from '@src/app/model/abstract-object';

@Component({
    selector: 'app-omc-settings',
    imports: [MatCardModule, TextEditComponent, MatButtonModule, MatIconModule],
    templateUrl: './omc-settings.component.html',
    styleUrl: './omc-settings.component.scss'
})
export class OmcSettingsComponent implements CanComponentDeactivate {
    canDeactivate = () => true;
    /** If you are editing here, you likely need to make same change in AgentSettingsService */
    omcSettingsSvc = inject(OmcSettingsService)
    roleSvc = inject(RoleService);
    dialog = inject(MatDialog);
    personSvc = inject(PersonService);
    cds = inject(ConfirmDialogService);
    cus = inject(CurrentUserService);
    private agentDataSvc = inject(AgentDataService);

    roles: Role[] = [];
    // Do not disable until picklist set correctly
    team = new OmcSettings();
    loaded = false;
    requestOwnerName: string;

    agentGrid = new GridField({
        field: { value: 'agents', cellOpts: { heading: 'Agents' }},
        rowFactory: this.agentFactory.bind(this),
        hasFooter: true
    });

    constructor() {
        this.omcSettingsSvc.getOne('omcSettings', null).subscribe(settings => this.setSettings(settings));
        // This should be done pre-edit and only allow agencies with permissions...
        this.roleSvc.get(true).subscribe( roles => {
            roles.forEach( r => this.roles.push(r as Role));
        })
    }

    setSettings(settings) {
        this.team = settings;
        this.loaded = true;
        if (settings.requestOwnerId !== 'agent') {
            this.personSvc.get<Person>(false, new HttpParams().set('userId', settings.requestOwnerId)).subscribe( contacts => {
                if (contacts && contacts.length === 1) {
                    this.requestOwnerName = contacts[0].fullName
                } else {
                    this.requestOwnerName = 'Invalid user set';
                }
            })
        } else {
            this.requestOwnerName = 'Agent';
        }
    }

    editName() {
        this.editField(FieldMaker.nameControl({ validators: [required] }));
    }

    /**
     * Support only, and only ever between agencies the OMC has already granted access to - this
     * promotes one of its existing agents, it does not create a grant. Everything raised from now
     * on is stamped with the new agency, and the OMC's bank accounts move across with it; requests
     * and actions already raised stay with the agency that did the work.
     */
    editPrimaryAgent() {
        const agentField = FormPicklistComponent.make('Primary Agent', 'agentTeamId', null, {
            items: this.team.agents ?? [],
            optionValue: (o: Agent) => o.agentTeamId,
            optionDisplayValue: (o: Agent) => o.agentName,
        }, { validators: [required] });

        const fieldSet = new FieldSet({ fields: [agentField], formLayout: LAYOUT_OPTIONS.singleCol });
        fieldSet.setValue(this.team as unknown as AbstractObject, false);

        this.dialog.open(FieldSetDialogComponent, {
            data: {
                title: $localize`Change Primary Agent`,
                saveText: $localize`Change Agent`,
                fieldSet,
                minWidth: '400px',
                minHeight: '200px',
                saveFn: (v: { agentTeamId: uuid }) => this.omcSettingsSvc.setPrimaryAgent(v.agentTeamId),
            } as FieldSetDialogOptions,
        }).afterClosed().subscribe(updated => {
            if (updated) {
                this.setSettings(updated);
                this.agentDataSvc.clearOmcList();
            }
        });
    }

    editDefaultRequestOwner() {
        this.editField(FormPicklistComponent.make('Default Request Owner', 'requestOwnerId', null, {
            service: this.personSvc, serviceFilter: new HttpParams().set('userId', 'NOT NULL'),
            optionDisplayValue: (o: Person) => o.fullName,
            optionValue: (o: Person) => o.userId,
        }));
    }    

    editPurchaseAttachments() {
        this.editField(FormPicklistComponent.make('Purchases Need Attachments', 'purchaseAttachments', null, {
            items: [{id:'required', name: 'Required'}, {id:'optional', name:'Optional'}]
        }));
    }

    editPurchaseApproval() {
        this.editField(FormPicklistComponent.make('Purchases Approvals', 'purchaseApprovals', null, {
            items: [
                {id:'manual', name: 'Manually Approve All Purchases before payment allowed'}, 
                {id:'automatic', name:'Automatically Mark All Invoices Approved For Payment'}]
        }));
    }

    editAiLevel() {
        this.editField(FormPicklistComponent.make('AI Level', 'aiLevel', null, {
            items: AI_LEVELS,
        }, { hint: AI_LEVEL_HINT }));
    }

    editShowOmcDashboard() {
        this.editField(FormPicklistComponent.make('Show OMC Dashboard', 'showOmcDashboard', null, {
            items: [{id: 'no', name: 'No'}, {id: 'directors', name: 'Directors'}]
        }));
    }

    editVatNumber() {
        this.editField(FormTextComponent.make('VAT Number', 'vatNumber', {
            hint: 'VAT Number if registered, leave blank if not registered. If you enter a VAT Number, the system will require you to enter VAT information on all budget codes and transactions going forward.'
        }));
    }

    editAddress() {
        this.editField(FormTextAreaComponent.make('Partial Unit Address', 'defaultAddress', {
            validators: [required, maxChars(255)],
        }))
    }

    editLegalName() {
        const field = FormTextComponent.make('Legal Company Name', 'legalName', { validators: [required] })
        this.editField(field);
    }

    editMailSendingName() {
       const field = FormTextComponent.make('The name to send mails as', 'mailSendingName',
            { validators: [required],
                hint: 'Usually you can leave the default value here, and let the system use the name of your OMC, but enter a value here to override it' })
        this.editField(field);
    }

    editMailSendingAccount() {
        const field = FormTextComponent.make('The email account to send mail', 'mailSendingAccount',
            {
                validators: [required],
                hint: 'Usually you can leave the default value here, and let the system use the ID of your OMC, the account used here must exist in your sending domain'
            })
        this.editField(field);
    }

    editPaymentTerms() {
        this.editField(FormTextAreaComponent.make('Payment Terms', 'paymentTerms',
            { validators: [required, maxChars(255)] }))
    }

    editServiceChargeLabel() {
        this.editField(FormTextComponent.make('Service Charge Label', 'serviceChargeLabel', {
            validators: [required, maxChars(60)],
            hint: 'What to call a Service Charge on PDF requests for payment and statements. Defaults to "Invoice Notice", a middle ground that reads as an invoice for OCR/approved-housing-body purposes without conceding it is one. Not used elsewhere in the app'
        }));
    }

    editCreditNoteLabel() {
        this.editField(FormTextComponent.make('Credit Note Label', 'creditNoteLabel', {
            validators: [required, maxChars(60)],
            hint: 'What to call a Credit Note on PDF requests for payment and statements. Defaults to "Credit Notice". Not used elsewhere in the app'
        }));
    }

    editBillingAddress() {
        this.editField(FormTextAreaComponent.make('Billing Address', 'billingAddress', {
            validators: [required, maxChars(255)],
         }))
    }

    editMailSendingDomain() {
        this.editField(FormTextComponent.make('Mail Sending Domain', 'mailSendingDomain', {
            hint: 'Must be configured by OurOMC',
            disable: !this.cus.hasUber(),
            validators: [required, maxChars(255)],
        }))
    }

    editMailReplyToDomain() {
        this.editField(FormTextComponent.make('Mail Reply To Domain', 'mailReplyToDomain', {
            hint: 'Must be configured by OurOMC',
            disable: !this.cus.hasUber(),
            validators: [required, maxChars(255)],
        }))
    }

    editField(field: Field) {
        const data = new EditFieldDialogOptions({
            field,
            id: 'omcSettings',
            service: this.omcSettingsSvc,
        });
        this.dialog.open(EditFieldDialogComponent,{data}).afterClosed().subscribe((updated) => {
            if (updated) {
                this.setSettings(updated);
                this.agentDataSvc.clearOmcList();
            }
        })
    }

    editReplyToMail() {
        const field = FormEmailComponent.make('Email Address if recipient presses reply', 'replyToMail',
            {
                validators: [required],
                hint: `Note: It can reduce your mails deliverability if the replyTo does not match the sender email address,
                especially if the address you use is a free account, e.g. gmail etc. Unless you really know what you are doing
                it is usually best to leave the replyToMail alone. If reply comes back to OurOMC we will create a request for you.
                `
            })
        this.editField(field);
    }

    deleteParameter(name: string, value: string | uuid) {

        if (!this.getParameter(name)) {
            this.cds.alert(
                $localize`Cannot remove parameter`,
                $localize`${value} is the default value for ${name}, you cannot remove the parameter, you can edit to override it`,
            );
        } else {
            this.cds.open(
                $localize`Remove Setting`,
                $localize`Do you wish to remove the '${name}' setting and revert to the default value`,
                () => this.omcSettingsSvc.deleteParameter(name, value, this.team).subscribe(x => {
                    this.setSettings(x);
                    this.agentDataSvc.clearOmcList();
                })
            );
        }
    }

    getParameter(name: string) {
        return this.team.parameters.find( p => p.name === name);
    }

    sendTestMail() {
        this.cds.open('Send Test', 'Send test mail from this OMC?', () => {
            this.omcSettingsSvc.sendTestMail(this.team);
        })

    }

    addAgent() {
        const dialogRef = this.dialog.open(OmcAddAgentComponent, {data: this.team});
        return dialogRef.afterClosed();
    }


    private agentFactory() {
        return [
            FormTextComponent.make('Agency Name', 'agentName', {disable: true}),
            FormTextComponent.make('Agency Personnel With Role', 'agentRoleName', {disable: true}),
            FormPicklistComponent.make('Are Granted', 'omcRoleId', null, {items: this.roles}, {disable: true}),
            //FieldMaker.spacer()
        ];
    }
}
